// Statistiques commerciales de l'accueil (`GET /dashboard/stats`), recalculées
// sur l'appareil avec les règles de backend/app/api/v1/dashboard.py et
// backend/app/core/metrics.py. Le rendu de l'accueil ne change pas: seule la
// source des chiffres bascule en local quand le réseau manque.
import { calculerSoldes } from './grandLivre';

const STATUTS_FACTURE_OUVERTE = new Set(['PENDING', 'SENT', 'PARTIAL', 'PARTIALLY_PAID', 'OVERDUE']);
const STATUTS_TRAJET_ACTIFS = new Set([
  'CREATED', 'EN_ROUTE_TO_BORDER', 'AT_CUSTOMS_EXIT', 'CLEARED_CUSTOMS_EXIT', 'EN_ROUTE_TO_LOADING',
  'LOADING', 'EN_ROUTE_TO_ENTRY_CUSTOMS', 'AT_CUSTOMS_ENTRY', 'CLEARED_CUSTOMS_ENTRY',
  'ARRIVED_AT_DEPOT', 'EN_ROUTE_TO_DELIVERY', 'DELIVERED',
]);

const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};
const maj = (valeur) => String(valeur ?? '').toUpperCase();

export const factureOuverte = (facture) => STATUTS_FACTURE_OUVERTE.has(maj(facture.status));
const montantOuvert = (facture) =>
  num(facture.amount_remaining !== null && facture.amount_remaining !== undefined ? facture.amount_remaining : facture.total_amount);
export const montantFacture = (facture) =>
  maj(facture.status) === 'CANCELLED' ? 0 : Math.max(num(facture.total_amount) - num(facture.credit_total), 0);

export function debutDePeriode(periode, maintenant) {
  const d = new Date(maintenant);
  if (periode === 'day') return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  if (periode === 'week') {
    const jour = (d.getUTCDay() + 6) % 7; // lundi = 0, comme Python
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - jour, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()));
  }
  if (periode === 'year') return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

export function calculerStatsAccueil({ periode = 'month', maintenant = new Date(), factures, commandes, clients, camions, trajets, convois, chauffeurs, mouvements }) {
  const debut = debutDePeriode(periode, maintenant);
  const debutTexte = debut.toISOString();

  const payees = factures.filter((f) => maj(f.status) === 'PAID' && String(f.invoice_date || '') >= debutTexte);
  const recette = payees.reduce((s, f) => s + montantFacture(f), 0);

  const commandesPeriode = commandes.filter((c) => String(c.created_at || '') >= debutTexte);
  const volume = commandesPeriode.reduce((s, c) => s + num(c.quantity), 0);

  const ouvertes = factures.filter(factureOuverte);
  const aEncaisser = ouvertes.reduce((s, f) => s + montantOuvert(f), 0);

  const recetteParClient = {};
  payees.forEach((f) => {
    if (f.client_id) recetteParClient[f.client_id] = (recetteParClient[f.client_id] || 0) + montantFacture(f);
  });
  const parId = Object.fromEntries(clients.map((c) => [c.id, c]));
  const meilleurs = Object.entries(recetteParClient)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .filter(([id]) => parId[id])
    .map(([id, revenue]) => ({ client_id: id, client_name: parId[id].name, revenue }));

  const camionsActifs = camions.filter((t) => t.status !== 'deleted' && !t.deleted);
  const statut = (t) => t.current_status || 'FREE';
  const libres = camionsActifs.filter((t) => statut(t) === 'FREE').length;
  const enTrajet = camionsActifs.filter((t) => statut(t) === 'ON_TRIP').length;
  const enAtelier = camionsActifs.filter((t) => statut(t) === 'MAINTENANCE').length;

  const trajetsVivants = trajets.filter((t) => !t.deleted);
  const actifs = trajetsVivants.filter((t) => STATUTS_TRAJET_ACTIFS.has(t.status));
  const aLaDouane = trajetsVivants.filter((t) => ['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(t.status));
  const convoisActifs = convois.filter((o) => o.active !== false);
  const chauffeursActifs = chauffeurs.filter((d) => !d.deleted).filter((d) => d.active !== false);

  const s = calculerSoldes(mouvements);
  // Même arithmétique que le serveur: ce calcul ignore IN_TRANSIT et la consommation interne.
  const mouvementsTypes = (type) => mouvements.filter((m) => m.movement_type === type).reduce((x, m) => x + num(m.quantity_liters), 0);
  const imports = mouvementsTypes('IMPORT');
  const ventes = mouvementsTypes('SALE');
  const transferts = mouvementsTypes('INTERNAL_TRANSFER');
  const pertes = mouvementsTypes('LOSS');
  const ajustements = mouvementsTypes('ADJUSTMENT');
  const courant = Math.max(0, imports - ventes - transferts - pertes + ajustements);
  const seuilBas = 20000;
  const seuilHaut = 2200000;
  const niveau = Math.min(100, Math.max(0, seuilHaut > 0 ? (courant / seuilHaut) * 100 : 0));
  const espace = (n) => String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  let alerte;
  if (courant <= seuilBas) alerte = ['low', 'Stock bas', `Sous le seuil bas (${espace(seuilBas)} L)`];
  else if (courant >= seuilHaut) alerte = ['high', 'Stock plein', `Au-dessus du seuil haut (${espace(seuilHaut)} L)`];
  else alerte = ['normal', 'Stock normal', 'Niveau de stock stable'];
  void s;

  return {
    period: periode,
    start_date: debut.toISOString(),
    end_date: new Date(maintenant).toISOString(),
    revenue: { total: recette, invoices_count: payees.length },
    orders: {
      total_count: commandesPeriode.length,
      total_volume_liters: volume,
      average_order_size: commandesPeriode.length ? volume / commandesPeriode.length : 0,
    },
    receivables: { total_unpaid: aEncaisser, unpaid_invoices_count: ouvertes.length },
    clients: { active_count: clients.filter((c) => c.status === 'ACTIVE').length, top_clients: meilleurs },
    trucks: { total: camionsActifs.length, free: libres, on_trip: enTrajet, maintenance: enAtelier },
    trips: { active: actifs.length, at_customs: aLaDouane.length },
    operations: { active: convoisActifs.length },
    drivers: { active: chauffeursActifs.length },
    stock: {
      current_liters: courant,
      rolling_level: niveau,
      thresholds: { low: seuilBas, high: seuilHaut },
      alert_level: alerte[0],
      alert_label: alerte[1],
      alert_message: alerte[2],
      distribution: { import: imports, sale: ventes, internal: transferts },
    },
  };
}
